/**
 * Workspace Messaging Integrations Settings Component
 * Manages Slack and Microsoft Teams integrations for the organization.
 * Provides OAuth connection, channel sync, and health monitoring.
 */

import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Trash2,
  Link,
  Unlink,
  Activity,
  Users,
  Hash,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Shield,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';

// Slack icon component
const SlackIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
  </svg>
);

// Teams icon component
const TeamsIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.625 8.073c.574 0 1.125-.23 1.53-.64.407-.408.636-.962.636-1.539a2.173 2.173 0 0 0-2.166-2.173 2.173 2.173 0 0 0-2.167 2.173c0 .577.23 1.131.636 1.54.405.408.956.639 1.531.639zM24 10.177v5.014c0 .389-.155.762-.43 1.037a1.465 1.465 0 0 1-1.035.43h-3.977v-5.25h4.975c.26 0 .467.21.467.47v-.7zM16.875 5.323a3.258 3.258 0 0 0 3.25-3.258A3.258 3.258 0 0 0 16.875-1.19a3.258 3.258 0 0 0-3.25 3.256 3.258 3.258 0 0 0 3.25 3.258zM15.188 6.48h3.374c.26 0 .469.21.469.47V11.408H14.72V6.949c0-.26.21-.47.468-.47zM11.03 6.948v10.24c0 .26-.21.469-.468.469H2.437a.469.469 0 0 1-.468-.47V6.95c0-.26.21-.47.468-.47h8.125c.259 0 .469.21.469.47zM8.392 3.692a2.606 2.606 0 0 0 2.6-2.606 2.606 2.606 0 0 0-2.6-2.606 2.606 2.606 0 0 0-2.6 2.606 2.606 2.606 0 0 0 2.6 2.606z" />
  </svg>
);

interface SlackConnection {
  id: number;
  organization_id: number;
  workspace_id: string;
  workspace_name: string;
  team_domain: string;
  status: string;
  bot_user_id: string | null;
  connected_at: string | null;
  connected_channels: number;
  synced_users: number;
}

interface TeamsConnection {
  id: number;
  organization_id: number;
  tenant_id: string;
  tenant_name: string;
  status: string;
  connected_at: string | null;
  connected_teams: number;
  synced_channels: number;
}

interface IntegrationHealth {
  provider: string;
  status: 'healthy' | 'degraded' | 'error';
  last_check: string;
  issues: string[];
  metrics: {
    messages_synced_24h: number;
    api_calls_24h: number;
    error_rate: number;
  };
}

interface WorkspaceMessagingIntegrationsProps {
  organizationId: number;
  onSettingsChange?: () => void;
}

export function WorkspaceMessagingIntegrations({
  organizationId,
  onSettingsChange,
}: WorkspaceMessagingIntegrationsProps) {
  const { fmt } = useFormattedDate();
  const [slackConnections, setSlackConnections] = useState<SlackConnection[]>([]);
  const [teamsConnections, setTeamsConnections] = useState<TeamsConnection[]>([]);
  const [health, setHealth] = useState<IntegrationHealth[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedProvider, setExpandedProvider] = useState<'slack' | 'teams' | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState<number | null>(null);

  useEffect(() => {
    fetchConnections();
    fetchHealth();
  }, [organizationId]);

  const fetchConnections = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch Slack connections
      try {
        const slackResponse = await authFetch(
          getWeaveinApiUrl('/api/integrations/slack/connections')
        );
        if (slackResponse.ok) {
          const slackData = await slackResponse.json();
          setSlackConnections(slackData);
        }
      } catch (e) {
        console.log('Slack API not available');
      }

      // Fetch Teams connections
      try {
        const teamsResponse = await authFetch(
          getWeaveinApiUrl('/api/integrations/teams/connections')
        );
        if (teamsResponse.ok) {
          const teamsData = await teamsResponse.json();
          setTeamsConnections(teamsData);
        }
      } catch (e) {
        console.log('Teams API not available');
      }
    } catch (err) {
      console.error('Error fetching connections:', err);
      setError('Failed to load workspace connections');
    } finally {
      setLoading(false);
    }
  };

  const fetchHealth = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await authFetch(getWeaveinApiUrl('/api/integrations/health'));
      if (response.ok) {
        const data = await response.json();
        setHealth(data.integrations || []);
      }
    } catch (e) {
      // Health endpoint might not exist yet
      console.log('Health check not available');
    }
  };

  const handleConnect = async (provider: 'slack' | 'teams') => {
    try {
      setConnecting(provider);
      const token = localStorage.getItem('auth_token');
      const response = await authFetch(getWeaveinApiUrl(`/api/integrations/${provider}/oauth/authorize?organization_id=${organizationId}`));

      if (response.ok) {
        const data = await response.json();
        // Redirect to OAuth flow
        window.location.href = data.authorization_url;
      } else {
        const errorData = await response.json();
        setError(errorData.detail || `Failed to initiate ${provider} connection`);
      }
    } catch (err) {
      console.error(`Error connecting to ${provider}:`, err);
      setError(`Failed to connect to ${provider}`);
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (provider: 'slack' | 'teams', connectionId: number) => {
    if (!confirm(`Are you sure you want to disconnect this ${provider === 'slack' ? 'Slack' : 'Teams'} workspace?`)) {
      return;
    }

    try {
      setDisconnecting(connectionId);
      const token = localStorage.getItem('auth_token');
      const response = await authFetch(getWeaveinApiUrl(`/api/integrations/${provider}/connections/${connectionId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchConnections();
        onSettingsChange?.();
      } else {
        setError('Failed to disconnect workspace');
      }
    } catch (err) {
      console.error('Error disconnecting:', err);
      setError('Failed to disconnect workspace');
    } finally {
      setDisconnecting(null);
    }
  };

  const getHealthStatus = (provider: string) => {
    return health.find(h => h.provider === provider);
  };

  const renderHealthBadge = (status?: string) => {
    if (!status) return null;

    const badges = {
      healthy: (
        <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full">
          <CheckCircle className="h-3 w-3" />
          Healthy
        </span>
      ),
      degraded: (
        <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs rounded-full">
          <AlertTriangle className="h-3 w-3" />
          Degraded
        </span>
      ),
      error: (
        <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded-full">
          <AlertCircle className="h-3 w-3" />
          Error
        </span>
      ),
    };

    return badges[status as keyof typeof badges] || null;
  };

  const renderSlackSection = () => {
    const isExpanded = expandedProvider === 'slack';
    const healthStatus = getHealthStatus('slack');
    const hasConnections = slackConnections.length > 0;

    return (
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
          onClick={() => setExpandedProvider(isExpanded ? null : 'slack')}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <SlackIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                Slack
                {healthStatus && renderHealthBadge(healthStatus.status)}
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {hasConnections
                  ? `${slackConnections.length} workspace${slackConnections.length > 1 ? 's' : ''} connected`
                  : 'Connect your Slack workspace'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasConnections ? (
              <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                <Link className="h-4 w-4" />
                Connected
              </span>
            ) : (
              <span className="flex items-center gap-1 text-sm text-gray-400">
                <Unlink className="h-4 w-4" />
                Not connected
              </span>
            )}
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/50">
            {/* Connected Workspaces */}
            {slackConnections.map((conn) => (
              <div
                key={conn.id}
                className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-4 border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h5 className="font-medium text-gray-900 dark:text-white">
                      {conn.workspace_name}
                    </h5>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {conn.team_domain}.slack.com
                    </p>
                  </div>
                  <button
                    onClick={() => handleDisconnect('slack', conn.id)}
                    disabled={disconnecting === conn.id}
                    className="text-red-600 hover:text-red-700 text-sm flex items-center gap-1"
                  >
                    {disconnecting === conn.id ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                    Disconnect
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <Hash className="h-4 w-4" />
                    <span>{conn.connected_channels} channels synced</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <Users className="h-4 w-4" />
                    <span>{conn.synced_users} users mapped</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <Clock className="h-4 w-4" />
                    <span>
                      {conn.connected_at
                        ? `Connected ${fmt.short(conn.connected_at)}`
                        : 'Not connected'}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {/* Connect Button */}
            <button
              onClick={() => handleConnect('slack')}
              disabled={connecting === 'slack'}
              className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-purple-500 hover:text-purple-600 dark:hover:text-purple-400 transition-colors flex items-center justify-center gap-2"
            >
              {connecting === 'slack' ? (
                <>
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <SlackIcon className="h-5 w-5" />
                  Connect Slack Workspace
                </>
              )}
            </button>

            {/* Security Info */}
            <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Shield className="h-4 w-4 text-purple-600 dark:text-purple-400 mt-0.5" />
                <div className="text-sm text-purple-700 dark:text-purple-300">
                  <p className="font-medium">Security Features</p>
                  <ul className="mt-1 space-y-0.5 text-purple-600 dark:text-purple-400">
                    <li>• OAuth 2.0 secure authentication</li>
                    <li>• Encrypted token storage</li>
                    <li>• Webhook signature verification</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTeamsSection = () => {
    const isExpanded = expandedProvider === 'teams';
    const healthStatus = getHealthStatus('teams');
    const hasConnections = teamsConnections.length > 0;

    return (
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
          onClick={() => setExpandedProvider(isExpanded ? null : 'teams')}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <TeamsIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                Microsoft Teams
                {healthStatus && renderHealthBadge(healthStatus.status)}
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {hasConnections
                  ? `${teamsConnections.length} tenant${teamsConnections.length > 1 ? 's' : ''} connected`
                  : 'Connect your Microsoft Teams tenant'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasConnections ? (
              <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                <Link className="h-4 w-4" />
                Connected
              </span>
            ) : (
              <span className="flex items-center gap-1 text-sm text-gray-400">
                <Unlink className="h-4 w-4" />
                Not connected
              </span>
            )}
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/50">
            {/* Connected Tenants */}
            {teamsConnections.map((conn) => (
              <div
                key={conn.id}
                className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-4 border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h5 className="font-medium text-gray-900 dark:text-white">
                      {conn.tenant_name}
                    </h5>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Tenant ID: {conn.tenant_id.slice(0, 8)}...
                    </p>
                  </div>
                  <button
                    onClick={() => handleDisconnect('teams', conn.id)}
                    disabled={disconnecting === conn.id}
                    className="text-red-600 hover:text-red-700 text-sm flex items-center gap-1"
                  >
                    {disconnecting === conn.id ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                    Disconnect
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <Users className="h-4 w-4" />
                    <span>{conn.connected_teams} teams synced</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <Hash className="h-4 w-4" />
                    <span>{conn.synced_channels} channels</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <Clock className="h-4 w-4" />
                    <span>
                      {conn.connected_at
                        ? `Connected ${fmt.short(conn.connected_at)}`
                        : 'Not connected'}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {/* Connect Button */}
            <button
              onClick={() => handleConnect('teams')}
              disabled={connecting === 'teams'}
              className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center justify-center gap-2"
            >
              {connecting === 'teams' ? (
                <>
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <TeamsIcon className="h-5 w-5" />
                  Connect Microsoft Teams
                </>
              )}
            </button>

            {/* Security Info */}
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div className="text-sm text-blue-700 dark:text-blue-300">
                  <p className="font-medium">Security Features</p>
                  <ul className="mt-1 space-y-0.5 text-blue-600 dark:text-blue-400">
                    <li>• Microsoft Graph API integration</li>
                    <li>• Automatic token refresh</li>
                    <li>• Encrypted credential storage</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-blue-600" />
          Workspace Messaging
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Connect Slack or Microsoft Teams for bidirectional message sync and team collaboration.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Integration Cards */}
      <div className="space-y-4">
        {renderSlackSection()}
        {renderTeamsSection()}
      </div>

      {/* Health Monitoring */}
      {health.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Integration Health
          </h4>
          <div className="grid grid-cols-2 gap-4">
            {health.map((h) => (
              <div
                key={h.provider}
                className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900 dark:text-white capitalize">
                    {h.provider}
                  </span>
                  {renderHealthBadge(h.status)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                  <p>Messages synced (24h): {h.metrics.messages_synced_24h}</p>
                  <p>API calls (24h): {h.metrics.api_calls_24h}</p>
                  <p>Error rate: {(h.metrics.error_rate * 100).toFixed(2)}%</p>
                </div>
                {h.issues.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                    {h.issues.map((issue, i) => (
                      <p key={i} className="text-xs text-red-600 dark:text-red-400">
                        • {issue}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
          Integration Benefits
        </h4>
        <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <li>• <strong>Bidirectional Sync:</strong> Messages sync between platform and workspace</li>
          <li>• <strong>User Mapping:</strong> Match workspace users to platform members</li>
          <li>• <strong>Channel Sync:</strong> Connect specific channels for focused collaboration</li>
          <li>• <strong>Reactions & Threads:</strong> Full conversation context preserved</li>
        </ul>
      </div>
    </div>
  );
}

export default WorkspaceMessagingIntegrations;
